import { useCallback, useEffect } from 'react'
import type maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { cn } from 'lib/utils'
import { useMapLibre } from '../hooks/useMapLibre'
import { SATELLITE_AVAILABLE, type Basemap } from '../config/mapConfig'
import BasemapSwitcher from './BasemapSwitcher'

export interface MapContainerProps {
	center?: [number, number]
	zoom?: number
	className?: string
	children?: React.ReactNode
	onMapLoad?: (map: maplibregl.Map) => void
	/**
	 * Hide the Map / Satellite control on this surface. The control is on by
	 * default so every map inherits it; pass this for an embedded map too small
	 * to spare the space. Ignored when no satellite key is configured, since
	 * there is nothing to switch to.
	 */
	hideBasemapSwitcher?: boolean
	/**
	 * What this map opens on when the user has never picked a basemap. Site
	 * capture passes 'satellite': a parcel, its frontage and its parking read
	 * off imagery in a way a street map cannot show.
	 */
	defaultBasemap?: Basemap
	/** See useMapLibre. A fixed basemap also hides the switcher. */
	fixedBasemap?: Basemap
	bearing?: number
	pitch?: number
	interactive?: boolean
	showControls?: boolean
	preserveDrawingBuffer?: boolean
}

export function MapContainer({
	center,
	zoom,
	className,
	children,
	onMapLoad,
	hideBasemapSwitcher = false,
	defaultBasemap,
	fixedBasemap,
	bearing,
	pitch,
	interactive,
	showControls,
	preserveDrawingBuffer,
}: MapContainerProps) {
	const { map, isLoaded, setContainer, basemap, setBasemap } = useMapLibre({
		center,
		zoom,
		defaultBasemap,
		fixedBasemap,
		bearing,
		pitch,
		interactive,
		showControls,
		preserveDrawingBuffer,
	})

	const containerRef = useCallback(
		(el: HTMLDivElement | null) => {
			setContainer(el)
		},
		[setContainer]
	)

	useEffect(() => {
		if (isLoaded && map) {
			onMapLoad?.(map)
		}
	}, [isLoaded, map, onMapLoad])

	return (
		<div
			ref={containerRef}
			className={cn('relative w-full h-full rounded-md overflow-hidden', className)}
		>
			{/*
			  * Bottom-right: top-right holds MapLibre's NavigationControl and
			  * bottom-left its ScaleControl, and surfaces put their own Layers
			  * panel top-left.
			  *
			  * bottom-12 rather than bottom-8: at 8 the switcher overlapped the
			  * attribution control by ~2px at BOTH 390px and 1440px. Easy to miss
			  * by eye, because the attribution background is translucent — it was
			  * caught by comparing bounding rects, not by looking.
			  */}
			{SATELLITE_AVAILABLE && !hideBasemapSwitcher && !fixedBasemap && (
				<BasemapSwitcher
					value={basemap}
					onChange={setBasemap}
					className="absolute bottom-12 right-2 z-10"
				/>
			)}
			{children}
		</div>
	)
}
